import React, { useState, useRef } from 'react';
import { Upload, Image as ImageIcon, X, Eye, RefreshCw, AlertCircle, Check } from 'lucide-react';
import { apiClient } from '../../services/api';
import { toAbs } from '../../utils/url';

interface ImageConfig {
  logoUrl?: string;
  backgroundImageUrl?: string;
  cardBackImageUrl?: string;
  faviconUrl?: string;
}

interface ImageUploaderProps {
  images: ImageConfig;
  onChange: (images: ImageConfig) => void;
  token?: string;
}

const IMAGE_TYPES: {
  key: keyof ImageConfig;
  label: string;
  desc: string;
  hint: string;
  maxBytes: number;
  ref: string;
}[] = [
  { key: 'logoUrl',         label: 'Logo do Site',    desc: 'Aparece no cabeçalho',              hint: '200×80 px · máx 2 MB',  maxBytes: 2*1024*1024, ref: 'logo' },
  { key: 'cardBackImageUrl',label: 'Verso das Cartas', desc: 'Fundo do verso das cartas de tarô', hint: '300×500 px · máx 2 MB',  maxBytes: 2*1024*1024, ref: 'cardBack' },
  { key: 'faviconUrl',      label: 'Favicon',          desc: 'Ícone na aba do navegador',         hint: '.ico / .png / .svg · máx 1 MB', maxBytes: 1*1024*1024, ref: 'favicon' },
];

const cardStyle: React.CSSProperties = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' };
const labelStyle: React.CSSProperties = { color: '#9ca3af' };
const mutedStyle: React.CSSProperties = { color: '#6b7280' };

const ImageUploader: React.FC<ImageUploaderProps> = ({ images, onChange, token }) => {
  const [uploading, setUploading] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const refs = {
    logo:     useRef<HTMLInputElement>(null),
    cardBack: useRef<HTMLInputElement>(null),
    favicon:  useRef<HTMLInputElement>(null),
  };

  const handleFile = async (imageType: keyof ImageConfig, file: File, maxBytes: number) => {
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Selecione apenas arquivos de imagem' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }
    if (file.size > maxBytes) {
      setMessage({ type: 'error', text: `Arquivo muito grande. Máximo: ${maxBytes / (1024*1024)} MB` });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    const effectiveToken = token ?? localStorage.getItem('token') ?? undefined;
    if (!effectiveToken) {
      setMessage({ type: 'error', text: 'Sessão expirada. Faça login novamente.' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    setUploading(imageType);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('image_type', imageType);

      const { data } = await apiClient.post('/theme/upload-image', form, {
        headers: { Authorization: `Bearer ${effectiveToken}` },
      });

      onChange({ ...images, [imageType]: data.url });
      setMessage({ type: 'success', text: data.message || 'Imagem enviada!' });
    } catch (err: any) {
      const raw = err?.response?.data?.detail;
      const msg = Array.isArray(raw)
        ? raw.map((r: any) => r?.msg ?? String(r)).join('; ')
        : typeof raw === 'string' ? raw : 'Erro no upload';
      setMessage({ type: 'error', text: msg });
    } finally {
      setUploading(null);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {IMAGE_TYPES.map(({ key, label, desc, hint, maxBytes, ref }) => {
          const current = images[key];
          const busy = uploading === key;

          return (
            <div key={key} className="rounded-xl p-4" style={cardStyle}>
              <div className="mb-3">
                <div className="text-sm font-medium text-white mb-0.5">{label}</div>
                <div className="text-xs mb-0.5" style={labelStyle}>{desc}</div>
                <div className="text-xs" style={mutedStyle}>{hint}</div>
              </div>

              {/* Preview / Drop zone */}
              {current ? (
                <div className="relative group mb-3">
                  <img
                    src={toAbs(current)}
                    alt={label}
                    className="w-full h-28 object-cover rounded-lg"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                    <button onClick={() => setPreviewImage(current)} className="p-2 rounded-lg bg-white/20 hover:bg-white/30">
                      <Eye className="w-4 h-4 text-white" />
                    </button>
                    <button onClick={() => onChange({ ...images, [key]: undefined })} className="p-2 rounded-lg bg-red-500/30 hover:bg-red-500/50">
                      <X className="w-4 h-4 text-white" />
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => refs[ref as keyof typeof refs].current?.click()}
                  className="h-28 rounded-lg flex flex-col items-center justify-center cursor-pointer transition-colors mb-3"
                  style={{ border: '2px dashed rgba(255,255,255,0.15)' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; }}
                >
                  {busy ? (
                    <RefreshCw className="w-5 h-5 animate-spin" style={{ color: '#6b7280' }} />
                  ) : (
                    <>
                      <ImageIcon className="w-5 h-5 mb-1" style={{ color: '#6b7280' }} />
                      <span className="text-xs" style={mutedStyle}>Clique para enviar</span>
                    </>
                  )}
                </div>
              )}

              <button
                onClick={() => refs[ref as keyof typeof refs].current?.click()}
                disabled={busy}
                className="w-full flex items-center justify-center gap-2 py-2 rounded-md text-white text-xs font-medium transition-opacity disabled:opacity-50"
                style={{ background: '#2563eb' }}
              >
                <Upload className="w-3.5 h-3.5" />
                {busy ? 'Enviando...' : current ? 'Alterar' : 'Enviar'}
              </button>

              <input
                ref={refs[ref as keyof typeof refs]}
                type="file"
                accept="image/*,.ico"
                className="hidden"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(key, f, maxBytes); }}
              />
            </div>
          );
        })}
      </div>

      {/* Status toast */}
      {message && (
        <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-500/15 text-green-300' : 'bg-red-500/15 text-red-300'}`}
          style={{ border: `1px solid ${message.type === 'success' ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}` }}>
          {message.type === 'success' ? <Check className="w-4 h-4 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
          {message.text}
        </div>
      )}

      {/* Preview modal */}
      {previewImage && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setPreviewImage(null)}>
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <img src={toAbs(previewImage)} alt="Preview" className="max-w-full max-h-[80vh] object-contain rounded-xl" />
            <button onClick={() => setPreviewImage(null)} className="absolute -top-3 -right-3 p-1.5 rounded-full bg-black/60 hover:bg-black/80">
              <X className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
